import { useEffect, useState } from "react";
import {
    Loader2,
    Upload,
    Trash2,
    Lock,
    LogOut,
    PencilLine,
    PlusCircle,
} from "lucide-react";
import { adminApi, getAdminToken, setAdminToken } from "../lib/api";
import { SubmissionsLog } from "../components/SubmissionsLog";

const SECTIONS = [
    { value: "commercial", label: "Commercial Projects" },
    { value: "residential", label: "Residential Renovations" },
    { value: "real-estate", label: "Real Estate Partner" },
    { value: "before-after", label: "Before / After Showcase" },
    { value: "general", label: "General" },
];

export default function Admin() {
    const [token, setTokenState] = useState(getAdminToken());
    const [password, setPassword] = useState("");
    const [loginError, setLoginError] = useState("");
    const [loginLoading, setLoginLoading] = useState(false);

    const [photos, setPhotos] = useState([]);
    const [pairs, setPairs] = useState([]);
    const [submissions, setSubmissions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [tab, setTab] = useState("photos");

    const refresh = async () => {
        setLoading(true);
        try {
            const [pRes, paRes, sRes] = await Promise.all([
                adminApi.get("/admin/photos"),
                adminApi.get("/admin/before-after"),
                adminApi.get("/admin/submissions"),
            ]);
            setPhotos(pRes.data || []);
            setPairs(paRes.data || []);
            setSubmissions(sRes.data || []);
        } catch (e) {
            if (e?.response?.status === 401) {
                setTokenState(null);
                setAdminToken(null);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (token) refresh();
    }, [token]);

    const onLogin = async (e) => {
        e.preventDefault();
        setLoginLoading(true);
        setLoginError("");
        try {
            const r = await adminApi.post("/admin/login", { password });
            setAdminToken(r.data.token);
            setTokenState(r.data.token);
            setPassword("");
        } catch (err) {
            setLoginError(err?.response?.data?.detail || "Login failed");
        } finally {
            setLoginLoading(false);
        }
    };

    const onLogout = () => {
        setAdminToken(null);
        setTokenState(null);
    };

    if (!token) {
        return (
            <main className="min-h-[100svh] bg-[#050505] flex items-center justify-center px-6 pt-24">
                <form
                    onSubmit={onLogin}
                    data-testid="admin-login-form"
                    className="w-full max-w-md border border-white/10 bg-[#0A0A0A] p-10"
                >
                    <Lock className="text-[#F5A623]" size={28} />
                    <h1 className="font-display text-3xl mt-6 text-white">
                        Admin Access
                    </h1>
                    <p className="text-white/55 text-sm mt-2">
                        Enter the admin password to manage project photos.
                    </p>
                    <label className="block mt-8">
                        <span className="overline">Password</span>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            data-testid="admin-password-input"
                            className="input-bevel mt-2"
                            required
                        />
                    </label>
                    {loginError && (
                        <div
                            data-testid="admin-login-error"
                            className="mt-4 text-sm text-red-400 border border-red-500/30 bg-red-500/10 px-4 py-2"
                        >
                            {loginError}
                        </div>
                    )}
                    <button
                        type="submit"
                        disabled={loginLoading}
                        data-testid="admin-login-button"
                        className="btn-primary w-full mt-6 justify-center disabled:opacity-60"
                    >
                        {loginLoading ? (
                            <Loader2 className="animate-spin" size={16} />
                        ) : (
                            "Sign In"
                        )}
                    </button>
                </form>
            </main>
        );
    }

    return (
        <main
            className="min-h-[100svh] bg-[#050505] pt-32 pb-24"
            data-testid="admin-dashboard"
        >
            <div className="container-bevel">
                <div className="flex items-center justify-between gap-6 flex-wrap">
                    <div>
                        <span className="overline">Admin</span>
                        <h1 className="font-display text-4xl text-white mt-3">
                            Project Manager
                        </h1>
                    </div>
                    <button
                        onClick={onLogout}
                        data-testid="admin-logout-button"
                        className="btn-secondary !px-5 !py-3 !text-xs"
                    >
                        <LogOut size={14} /> Sign out
                    </button>
                </div>

                <div className="mt-10 flex gap-px bg-white/10 border border-white/10 w-fit">
                    <button
                        onClick={() => setTab("photos")}
                        data-testid="admin-tab-photos"
                        className={`px-6 py-3 text-xs uppercase tracking-[0.2em] ${
                            tab === "photos"
                                ? "bg-[#F5A623] text-black"
                                : "bg-[#0A0A0A] text-white/70 hover:text-white"
                        }`}
                    >
                        Project Photos ({photos.length})
                    </button>
                    <button
                        onClick={() => setTab("pairs")}
                        data-testid="admin-tab-pairs"
                        className={`px-6 py-3 text-xs uppercase tracking-[0.2em] ${
                            tab === "pairs"
                                ? "bg-[#F5A623] text-black"
                                : "bg-[#0A0A0A] text-white/70 hover:text-white"
                        }`}
                    >
                        Before / After ({pairs.length})
                    </button>
                    <button
                        onClick={() => setTab("submissions")}
                        data-testid="admin-tab-submissions"
                        className={`px-6 py-3 text-xs uppercase tracking-[0.2em] ${
                            tab === "submissions"
                                ? "bg-[#F5A623] text-black"
                                : "bg-[#0A0A0A] text-white/70 hover:text-white"
                        }`}
                    >
                        Contact Log ({submissions.length})
                    </button>
                </div>

                {loading && (
                    <div className="mt-8 flex items-center gap-3 text-white/60">
                        <Loader2 className="animate-spin" size={16} /> Loading…
                    </div>
                )}

                {tab === "photos" && (
                    <PhotosManager photos={photos} onRefresh={refresh} />
                )}
                {tab === "pairs" && (
                    <PairsManager pairs={pairs} onRefresh={refresh} />
                )}
                {tab === "submissions" && (
                    <SubmissionsLog items={submissions} onRefresh={refresh} />
                )}
            </div>
        </main>
    );
}

// --- Photos manager -------------------------------------------------------
function PhotosManager({ photos, onRefresh }) {
    const [file, setFile] = useState(null);
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [category, setCategory] = useState("");
    const [section, setSection] = useState("commercial");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const onUpload = async (e) => {
        e.preventDefault();
        if (!file) return;
        setBusy(true);
        setErr("");
        try {
            const fd = new FormData();
            fd.append("file", file);
            fd.append("title", title);
            fd.append("description", description);
            fd.append("category", category);
            fd.append("section", section);
            await adminApi.post("/admin/photos", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setFile(null);
            setTitle("");
            setDescription("");
            setCategory("");
            await onRefresh();
        } catch (e2) {
            setErr(e2?.response?.data?.detail || "Upload failed");
        } finally {
            setBusy(false);
        }
    };

    const onDelete = async (id) => {
        if (!window.confirm("Delete this photo?")) return;
        await adminApi.delete(`/admin/photos/${id}`);
        await onRefresh();
    };

    const onUpdate = async (id, patch) => {
        await adminApi.patch(`/admin/photos/${id}`, patch);
        await onRefresh();
    };

    return (
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
            <form
                onSubmit={onUpload}
                data-testid="admin-photo-upload-form"
                className="lg:col-span-4 border border-white/10 bg-[#0A0A0A] p-6 self-start"
            >
                <div className="flex items-center gap-2 text-[#F5A623]">
                    <Upload size={18} />
                    <span className="overline">Upload Photo</span>
                </div>
                <label className="block mt-5">
                    <span className="text-xs uppercase tracking-[0.2em] text-white/60">
                        Image File
                    </span>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                        data-testid="admin-photo-file-input"
                        className="block mt-2 w-full text-sm text-white/70 file:mr-3 file:bg-[#F5A623] file:text-black file:border-0 file:px-4 file:py-2 file:font-semibold file:text-xs file:uppercase file:tracking-[0.15em] file:cursor-pointer"
                        required
                    />
                </label>
                <label className="block mt-4">
                    <span className="text-xs uppercase tracking-[0.2em] text-white/60">
                        Title
                    </span>
                    <input
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="input-bevel mt-2"
                        data-testid="admin-photo-title-input"
                    />
                </label>
                <label className="block mt-4">
                    <span className="text-xs uppercase tracking-[0.2em] text-white/60">
                        Description
                    </span>
                    <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={2}
                        className="input-bevel mt-2 resize-none"
                        data-testid="admin-photo-description-input"
                    />
                </label>
                <label className="block mt-4">
                    <span className="text-xs uppercase tracking-[0.2em] text-white/60">
                        Category
                    </span>
                    <input
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        placeholder="e.g. Kitchen, Office, Exterior"
                        className="input-bevel mt-2"
                        data-testid="admin-photo-category-input"
                    />
                </label>
                <label className="block mt-4">
                    <span className="text-xs uppercase tracking-[0.2em] text-white/60">
                        Section
                    </span>
                    <select
                        value={section}
                        onChange={(e) => setSection(e.target.value)}
                        className="input-bevel mt-2"
                        data-testid="admin-photo-section-select"
                    >
                        {SECTIONS.map((s) => (
                            <option key={s.value} value={s.value}>
                                {s.label}
                            </option>
                        ))}
                    </select>
                </label>
                {err && (
                    <div className="mt-4 text-sm text-red-400 border border-red-500/30 bg-red-500/10 px-3 py-2">
                        {err}
                    </div>
                )}
                <button
                    type="submit"
                    disabled={busy || !file}
                    data-testid="admin-photo-upload-submit"
                    className="btn-primary w-full mt-6 justify-center disabled:opacity-60"
                >
                    {busy ? (
                        <Loader2 className="animate-spin" size={16} />
                    ) : (
                        <>
                            <Upload size={14} /> Upload
                        </>
                    )}
                </button>
            </form>

            <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10 self-start">
                {photos.length === 0 && (
                    <div className="bg-[#0A0A0A] p-10 col-span-full text-center text-white/40">
                        No photos yet. Upload your first project photo.
                    </div>
                )}
                {photos.map((p) => (
                    <PhotoRow
                        key={p.id}
                        photo={p}
                        onDelete={() => onDelete(p.id)}
                        onUpdate={(patch) => onUpdate(p.id, patch)}
                    />
                ))}
            </div>
        </div>
    );
}

function PhotoRow({ photo, onDelete, onUpdate }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState({
        title: photo.title || "",
        description: photo.description || "",
        category: photo.category || "",
        section: photo.section || "general",
    });

    const save = async () => {
        await onUpdate(draft);
        setEditing(false);
    };

    return (
        <div className="bg-[#0A0A0A] p-5">
            <div className="aspect-[4/3] bg-black overflow-hidden">
                <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${photo.storage_path}`}
                    alt={photo.title}
                    className="w-full h-full object-cover"
                />
            </div>
            {!editing ? (
                <div className="mt-4 space-y-1">
                    <div className="font-display text-base text-white">
                        {photo.title || "(Untitled)"}
                    </div>
                    <div className="text-xs text-white/50">
                        {photo.section} · {photo.category || "—"}
                    </div>
                    {photo.description && (
                        <div className="text-sm text-white/60 line-clamp-2">
                            {photo.description}
                        </div>
                    )}
                </div>
            ) : (
                <div className="mt-4 space-y-3">
                    <input
                        className="input-bevel"
                        placeholder="Title"
                        value={draft.title}
                        onChange={(e) =>
                            setDraft({ ...draft, title: e.target.value })
                        }
                    />
                    <textarea
                        rows={2}
                        className="input-bevel resize-none"
                        placeholder="Description"
                        value={draft.description}
                        onChange={(e) =>
                            setDraft({ ...draft, description: e.target.value })
                        }
                    />
                    <input
                        className="input-bevel"
                        placeholder="Category"
                        value={draft.category}
                        onChange={(e) =>
                            setDraft({ ...draft, category: e.target.value })
                        }
                    />
                    <select
                        className="input-bevel"
                        value={draft.section}
                        onChange={(e) =>
                            setDraft({ ...draft, section: e.target.value })
                        }
                    >
                        {SECTIONS.map((s) => (
                            <option key={s.value} value={s.value}>
                                {s.label}
                            </option>
                        ))}
                    </select>
                </div>
            )}
            <div className="flex items-center gap-2 mt-4">
                {!editing ? (
                    <button
                        onClick={() => setEditing(true)}
                        data-testid={`admin-photo-edit-${photo.id}`}
                        className="text-xs uppercase tracking-[0.15em] px-3 py-2 border border-white/15 text-white/80 hover:border-[#F5A623] hover:text-[#F5A623] inline-flex items-center gap-2"
                    >
                        <PencilLine size={14} /> Edit
                    </button>
                ) : (
                    <>
                        <button
                            onClick={save}
                            className="text-xs uppercase tracking-[0.15em] px-3 py-2 bg-[#F5A623] text-black"
                        >
                            Save
                        </button>
                        <button
                            onClick={() => setEditing(false)}
                            className="text-xs uppercase tracking-[0.15em] px-3 py-2 border border-white/15 text-white/70"
                        >
                            Cancel
                        </button>
                    </>
                )}
                <button
                    onClick={onDelete}
                    data-testid={`admin-photo-delete-${photo.id}`}
                    className="ml-auto text-xs uppercase tracking-[0.15em] px-3 py-2 border border-red-500/30 text-red-400 hover:bg-red-500/10 inline-flex items-center gap-2"
                >
                    <Trash2 size={14} /> Delete
                </button>
            </div>
        </div>
    );
}

// --- Before/After pairs manager ------------------------------------------
function PairsManager({ pairs, onRefresh }) {
    const [draft, setDraft] = useState({
        title: "",
        location: "",
        before_url: "",
        after_url: "",
        order: pairs.length + 1,
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    const onCreate = async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr("");
        try {
            await adminApi.post("/admin/before-after", draft);
            setDraft({
                title: "",
                location: "",
                before_url: "",
                after_url: "",
                order: pairs.length + 2,
            });
            await onRefresh();
        } catch (e2) {
            setErr(e2?.response?.data?.detail || "Create failed");
        } finally {
            setBusy(false);
        }
    };

    const onDelete = async (id) => {
        if (!window.confirm("Delete this before/after pair?")) return;
        await adminApi.delete(`/admin/before-after/${id}`);
        await onRefresh();
    };

    const onToggle = async (p) => {
        await adminApi.patch(`/admin/before-after/${p.id}`, {
            is_active: !p.is_active,
        });
        await onRefresh();
    };

    return (
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
            <form
                onSubmit={onCreate}
                data-testid="admin-pair-create-form"
                className="lg:col-span-4 border border-white/10 bg-[#0A0A0A] p-6 self-start"
            >
                <div className="flex items-center gap-2 text-[#F5A623]">
                    <PlusCircle size={18} />
                    <span className="overline">New Before / After</span>
                </div>
                {[
                    { k: "title", label: "Title", type: "text" },
                    { k: "location", label: "Location", type: "text" },
                    { k: "before_url", label: "Before Image URL", type: "url" },
                    { k: "after_url", label: "After Image URL", type: "url" },
                    { k: "order", label: "Order", type: "number" },
                ].map((f) => (
                    <label className="block mt-4" key={f.k}>
                        <span className="text-xs uppercase tracking-[0.2em] text-white/60">
                            {f.label}
                        </span>
                        <input
                            type={f.type}
                            value={draft[f.k]}
                            onChange={(e) =>
                                setDraft({
                                    ...draft,
                                    [f.k]:
                                        f.type === "number"
                                            ? Number(e.target.value)
                                            : e.target.value,
                                })
                            }
                            className="input-bevel mt-2"
                            required={f.k !== "location"}
                        />
                    </label>
                ))}
                {err && (
                    <div className="mt-4 text-sm text-red-400 border border-red-500/30 bg-red-500/10 px-3 py-2">
                        {err}
                    </div>
                )}
                <button
                    type="submit"
                    disabled={busy}
                    data-testid="admin-pair-create-submit"
                    className="btn-primary w-full mt-6 justify-center disabled:opacity-60"
                >
                    {busy ? (
                        <Loader2 className="animate-spin" size={16} />
                    ) : (
                        "Create Pair"
                    )}
                </button>
                <p className="text-xs text-white/40 mt-4 leading-relaxed">
                    Tip: Use direct image URLs from your media host. Images
                    uploaded via "Project Photos" can be referenced through the
                    /api/files path.
                </p>
            </form>

            <div className="lg:col-span-8 space-y-4">
                {pairs.length === 0 && (
                    <div className="border border-white/10 bg-[#0A0A0A] p-10 text-center text-white/40">
                        No before/after pairs yet.
                    </div>
                )}
                {pairs.map((p) => (
                    <div
                        key={p.id}
                        className="border border-white/10 bg-[#0A0A0A] p-5 flex flex-col md:flex-row gap-4 items-start"
                    >
                        <div className="grid grid-cols-2 gap-1 w-full md:w-auto">
                            <img
                                src={p.before_url}
                                alt="before"
                                className="w-full md:w-32 h-24 object-cover"
                            />
                            <img
                                src={p.after_url}
                                alt="after"
                                className="w-full md:w-32 h-24 object-cover"
                            />
                        </div>
                        <div className="flex-1">
                            <div className="font-display text-lg text-white">
                                {p.title}
                            </div>
                            <div className="text-xs text-white/50 mt-1">
                                {p.location || "—"} · order {p.order} ·{" "}
                                {p.is_active ? "Active" : "Hidden"}
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => onToggle(p)}
                                className="text-xs uppercase tracking-[0.15em] px-3 py-2 border border-white/15 text-white/80 hover:border-[#F5A623] hover:text-[#F5A623]"
                            >
                                {p.is_active ? "Hide" : "Show"}
                            </button>
                            <button
                                onClick={() => onDelete(p.id)}
                                className="text-xs uppercase tracking-[0.15em] px-3 py-2 border border-red-500/30 text-red-400 hover:bg-red-500/10 inline-flex items-center gap-2"
                            >
                                <Trash2 size={14} /> Delete
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
